import { Box } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import style from '../partners.modules.scss'
import { Table } from 'components/core-ui/Table'
import { useNavigate } from 'react-router-dom'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { columns, getFilters, toolbarActions } from './helpers'
import rowActions from '../helpers'
import EditManagerModal from './EditManager/EditManagerModal'
import { DeleteModal } from './components/DeleteModal'
import { Link } from 'components/core-ui/Link'
import { makeCoreGetRequest } from 'utils/api'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'

const Managers: React.FC = () => {
  const [modalState, setModalState] = useState({
    isEditModalOpen: false,
    isDisableModalOpen: false,
    selectedRowId: null
  })
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)

  const { canCreate, canEdit, canDelete } = { canCreate: true, canEdit: true, canDelete: false }

  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const openEditModal = useCallback(
    (jobId?: number) => {
      setModalState({ ...modalState, isEditModalOpen: true, selectedRowId: jobId })
    },
    [modalState]
  )

  const closeEditModal = useCallback(() => {
    setModalState({ ...modalState, isEditModalOpen: false, selectedRowId: null })
    // refetch()
  }, [modalState])

  const openDisableModal = useCallback(
    (jobId: number) => {
      setModalState({ ...modalState, isDisableModalOpen: true, selectedRowId: jobId })
    },
    [modalState]
  )

  const closeDisableModal = useCallback(() => {
    setModalState({ ...modalState, isDisableModalOpen: false, selectedRowId: null })
    setShouldRefreshData(true)
  }, [modalState])

  const selectRow = useCallback(
    (rowId: number, action: string) => {
      action === 'edit' ? openEditModal(rowId) : openDisableModal(rowId)
    },
    [openEditModal, openDisableModal]
  )

  const [fetchParams, setFetchParams] = useState({
    page: 0,
    rowsPerPage: 10,
    order: 'asc',
    orderBy: 'id',
    tableFilters: [],
    searchText: ''
  })

  const toolbar = useMemo(
    () => toolbarActions({ canCreate, navigate, openModal: openEditModal }),
    [canCreate, navigate, openEditModal]
  )

  const filters = useMemo(() => getFilters(), [])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/managers',
          tableFilters,
          searchText,
          searchFields: ['firstName', 'lastName', 'fullName', 'email'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching managers. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>Managers</span>
      </div>
      {canEdit && (
        <EditManagerModal
          isModalOpen={modalState.isEditModalOpen}
          onModalClose={closeEditModal}
          managerId={modalState.selectedRowId}
          onSuccess={() => setShouldRefreshData(true)}
        />
      )}
      {canDelete && (
        <DeleteModal
          isModalOpen={modalState.isDisableModalOpen}
          closeModal={closeDisableModal}
          itemId={modalState.selectedRowId}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <Box>
        <Table
          hideCheckbox
          hasSearchInput
          orderBy="id"
          order="desc"
          columns={columns}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          shouldResetPage={shouldResetPage}
          setShouldResetPage={setShouldResetPage}
          rowActions={rowActions({
            canEdit,
            canDelete,
            navigate,
            selectRow,
            detailsUrl: '/iam/externalPartners/managers'
          })}
          rowActionsWidth="130px"
          desktopRowActionsWidth="100px"
          toolbarActions={toolbar}
          filters={filters}
        />
      </Box>
    </div>
  )
}

export default Managers
